import { viewportWidth as layoutViewportWidth } from "../utils/displayDensity";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Flame, Play, X } from "lucide-react";
import ExpandedVideo from "./ExpandedVideo";
import { MGIconButton, MGTechFrame } from "./editorial/MGEditorial";
import "./MindshareTopGrowth.css";
import "./gameArtCards.css";

export default function MindshareTopGrowth({ games, getVideoEmbed, onDismiss, showSpotlight = true, renderSkeleton, heightOverride, onSlotHeightChange, editorial = false, loading = false }) {
  const [activeIndex, setActiveIndex] = useState(0);
  const [videoOpen, setVideoOpen] = useState(false);
  const [cardWidth, setCardWidth] = useState(200);
  const sectionRef = useRef(null);
  const hoverRef = useRef(false);
  const active = games[activeIndex] || games[0];
  const video = active?.videoUrl ? getVideoEmbed(active.videoUrl) : null;

  useEffect(() => {
    if (games.length < 2 || videoOpen) return undefined;
    const interval = setInterval(() => {
      if (!hoverRef.current) setActiveIndex((index) => (index + 1) % games.length);
    }, 7000);
    return () => clearInterval(interval);
  }, [games.length, videoOpen]);


  useLayoutEffect(() => {
    const section = sectionRef.current;
    if (!section) return undefined;
    const applyWidth = (availableWidth) => {
      const columns = Math.max(1, Math.floor((availableWidth + 16) / (200 + 16)));
      const width = (availableWidth - 16 * (columns - 1)) / columns;
      setCardWidth((previous) => Math.abs(previous - width) > 0.1 ? width : previous);
    };
    // Measure before first paint so the slot never opens at the 200px fallback and then grows.
    applyWidth(parseFloat(getComputedStyle(section).width) || section.clientWidth);
    if (typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(([entry]) => applyWidth(entry.contentRect.width));
    observer.observe(section);
    return () => observer.disconnect();
  }, []);

  const cardHeight = (cardWidth - 2) * (380 / 285) + 2;
  const cardsHeight = 2 * cardHeight + 16;
  const isStacked = layoutViewportWidth() <= 1500;
  const isMobile = layoutViewportWidth() <= 600;
  const slotHeight = heightOverride ?? (cardsHeight + (isStacked ? (isMobile ? 236 : 246) : 0));

  const placeholder = !active && loading;

  useLayoutEffect(() => {
    if (active || placeholder) onSlotHeightChange?.(slotHeight);
  }, [active, placeholder, slotHeight, onSlotHeightChange]);

  if (!active && !placeholder) return null;
  const quote = active?.writeUp || active?.description;
  const growthLabel = active ? `${active.growthPT > 0 ? "+" : ""}${Math.round(active.growthPT * 100)}%` : "";

  return (
    <>
    <div
      ref={sectionRef}
      className="mg-top-growth-slot"
      style={{
        "--mg-top-growth-card-width": `${cardWidth}px`,
        "--mg-top-growth-card-height": `${cardHeight}px`,
        "--mg-top-growth-cards-height": `${cardsHeight}px`,
        "--mg-top-growth-slot-height": `${slotHeight}px`,
      }}
    >
    {placeholder ? (
    // Holds the final grid/spotlight geometry so Ranked Games never shifts when momentum data arrives.
    <section className="mg-top-growth is-loading" aria-label="Loading top mindSHARE growth" aria-busy="true">
      <div className="mg-top-growth-body">
        <div className="mg-top-growth-rankings">
          <div className="mg-top-growth-grid">
            {Array.from({ length: 10 }, (_, index) => <span key={index} className="mg-top-growth-card mg-game-art-card is-placeholder" style={{ "--i": index }} />)}
          </div>
        </div>
        <div className="mg-top-growth-spotlight is-placeholder" />
      </div>
    </section>
    ) : showSpotlight ? (
    <section
      className="mg-top-growth"
      aria-label="Top 10 mindSHARE growth"
      onMouseEnter={() => { hoverRef.current = true; }}
      onMouseLeave={() => { hoverRef.current = false; }}
    >
      <MGIconButton className="mg-top-growth-dismiss" onClick={onDismiss} aria-label="Dismiss Top Growth" title="Dismiss Top Growth">
        <X size={18} />
      </MGIconButton>

      <div className="mg-top-growth-body">
        <div className="mg-top-growth-rankings">
          <div className="mg-top-growth-grid">
            {games.slice(0, 10).map((game, index) => (
              <button
                key={game.id || game.name}
                type="button"
                className={`mg-top-growth-card mg-game-art-card${index === activeIndex ? " is-active" : ""}`}
                onClick={() => setActiveIndex(index)}
                aria-pressed={index === activeIndex}
                aria-label={`${game.name}, ${Math.round(game.growthPT * 100)}% growth`}
              >
                {game.imageUrl && <img src={game.imageUrl} alt="" loading="lazy" />}
                <span className="mg-top-growth-card-shade" />
                <span className="mg-top-growth-rank" aria-hidden="true"><Flame size={15} fill="currentColor" /></span>
              </button>
            ))}
          </div>
        </div>

        <MGTechFrame as="article"
          className="mg-top-growth-spotlight mg-game-art-card"
          aria-live="polite"
        >
          <div
            className="mg-top-growth-spotlight-art"
            aria-hidden="true"
            style={(active.backgroundUrl || active.imageUrl) ? { backgroundImage: `url("${active.backgroundUrl || active.imageUrl}")` } : undefined}
          />
          <div className="mg-top-growth-spotlight-shade" />
          <div className="mg-top-growth-spotlight-content">
            <div className="mg-top-growth-kicker">#{activeIndex + 1} · TOP MINDshare GROWTH</div>
            <h3>{active.name}</h3>
            <div className="mg-top-growth-value">{growthLabel}</div>
            <blockquote>{quote || "No editorial note available."}</blockquote>
            {(active.publisher || active.developer) && (
              <p className="mg-top-growth-credit">{[active.developer, active.publisher].filter(Boolean).join(" · ")}</p>
            )}
          </div>
          {video && (
            <MGIconButton
              className="mg-top-growth-play"
              onClick={() => setVideoOpen(true)}
              aria-label={`Play ${active.name} video`}
              title="Play video"
            >
              <Play size={14} fill="currentColor" />
            </MGIconButton>
          )}
        </MGTechFrame>
      </div>

      {videoOpen && video && (
        <ExpandedVideo video={video} title={active.name} onClose={() => setVideoOpen(false)} />
      )}
    </section>
    ) : (
      renderSkeleton?.(slotHeight)
    )}
    </div>
    </>
  );
}